// client/src/coms/components/mobile/gestureRules.js
// Phone swipe gestures (Mobile Layout → Gestures): which swipe does what, where a swipe may start, and how a
// finished touch is read. Swipes never start where something else needs the finger: text fields, selected text,
// canvases, video, sliders, sideways-scrolling rows, dialogs and menus, or the screen edges Android uses for Back.
// Every action also has a button, so nothing depends on a gesture.

export const GESTURE_DIRECTIONS = [
  { id: 'swipeRight', label: 'Swipe right', hint: 'Anywhere in the conversation, away from the screen edges.' },
  { id: 'swipeLeft', label: 'Swipe left', hint: 'Anywhere in the conversation, away from the screen edges.' },
  { id: 'swipeUp', label: 'Swipe up from the navigation', hint: 'Starting on the bottom bar, menu button or handle.' },
  { id: 'swipeDown', label: 'Swipe down on the header', hint: 'Starting on the top bar with the conversation name.' },
];

export const GESTURE_ACTIONS = [
  { id: 'none', label: 'Nothing' },
  { id: 'servers', label: 'Servers and channels', alt: 'the server button at the top left' },
  { id: 'members', label: 'Members', alt: 'the members button at the top right' },
  { id: 'chats', label: 'Chats', alt: 'Chats in your navigation' },
  { id: 'switcher', label: 'Switch conversation', alt: 'the ⇄ button at the top' },
  { id: 'menu', label: 'Navigation menu', alt: 'More, the menu button or the handle' },
  { id: 'prev', label: 'Previous conversation', alt: 'the ⇄ switcher' },
  { id: 'next', label: 'Next conversation', alt: 'the ⇄ switcher' },
];

// One action per direction, and an action on only one direction: choosing an action already used elsewhere moves
// it here and leaves the other direction doing nothing (so two swipes never do the same thing by accident).
export function assignGesture(gestures, dir, action) {
  const next = { ...gestures };
  if (action !== 'none') for (const k of Object.keys(next)) if (k !== dir && next[k] === action) next[k] = 'none';
  next[dir] = action;
  return next;
}

const BLOCKING = [
  'input',
  'textarea',
  'select',
  '[contenteditable="true"]',
  '[data-coms-no-swipe="true"]',
  'canvas',
  'video',
  'audio',
  'iframe',
  '[role="slider"]',
  '[role="dialog"]',
  '.vx-mmenu',
  '.vx-msheet',
].join(', ');

const EDGE = 24; // px from the left and right edges reserved for the system Back gesture

function scrollsSideways(el, stopAt) {
  for (let n = el; n && n !== stopAt && n.nodeType === 1; n = n.parentElement) {
    if (n.scrollWidth > n.clientWidth + 2) {
      const ox = window.getComputedStyle(n).overflowX;
      if (ox === 'auto' || ox === 'scroll') return true;
    }
  }
  return false;
}

// Where a touch starts: whether a sideways swipe may begin there, and whether it is on the bottom navigation or the
// header (the only places vertical swipes are read, so message scrolling is never taken over).
export function swipeStartInfo(target, x, viewportWidth, root) {
  const none = { horizontal: false, zone: null };
  if (!target || typeof target.closest !== 'function') return none;
  if (target.closest(BLOCKING)) return none;
  try {
    const sel = window.getSelection && window.getSelection();
    if (sel && String(sel).trim()) return none;
  } catch {}
  const zone = target.closest('[data-testid="mobile-nav"][data-position="bottom"], .vx-mhandle-wrap, .vx-mlaunch-wrap')
    ? 'bottom'
    : target.closest('[data-testid="mobile-header"]')
      ? 'header'
      : null;
  const horizontal = x >= EDGE && x <= viewportWidth - EDGE && !scrollsSideways(target, root);
  return { horizontal, zone };
}

// Reads a finished touch: a clear sideways swipe (anywhere allowed), or a clear vertical one in its zone.
export function resolveSwipe({ dx, dy, horizontal, zone }) {
  const adx = Math.abs(dx);
  const ady = Math.abs(dy);
  if (horizontal && adx >= 60 && adx > ady * 1.4) return dx > 0 ? 'swipeRight' : 'swipeLeft';
  if (ady >= 40 && ady > adx * 1.4) {
    if (zone === 'bottom' && dy < 0) return 'swipeUp';
    if (zone === 'header' && dy > 0) return 'swipeDown';
  }
  return null;
}
